import React from 'react';
import TextInput from '../components/TextInput';
import WheelDatePicker from '../components/WheelDatePicker';
import ChoicePicker from '../components/ChoicePicker';
import MultiChoicePicker from '../components/MultiChoicePicker';
import SearchableListPicker from '../components/SearchableListPicker';
import JpAddressInput from '../components/JpAddressInput';
import { sendIcon } from '../chatIcons';
import type {
  DisabilityCategory,
  FamilyStatusFlag,
  MaritalStatus,
  Step,
  YesNo,
} from './useGeneralProfileFlow.ts';

export interface GeneralProfileChatComposerProps {
  t: (key: string) => string;
  wheelLocale: string;
  step: Step;

  // values
  legalFamily: string;
  legalFirst: string;
  legalMiddle: string;
  kanaFamily: string;
  kanaFirst: string;
  kanaMiddle: string;
  dob: { year: number; month: number; day: number };
  gender: 'male' | 'female' | 'other' | 'unknown';
  nationality: string;
  phoneNumber: string;
  homePhoneNumber: string;
  homePhoneSkipped: boolean;
  address: { postalCode: string; pref: string; city: string; town: string; line2: string };
  email: string;
  residenceCardNumber: string;
  myNumber: string;
  myNumberSkipped: boolean;
  maritalStatus: MaritalStatus | '';
  familyStatusSkipped: boolean;
  familyStatusSelections: FamilyStatusFlag[];
  disabilityCategory: DisabilityCategory | '';
  disabilityFacts: string;
  showWidowedFamilyOption: boolean;
  emergencyHasContact: YesNo | '';
  emergencyName: string;
  emRelationship: string;
  emergencyPhone: string;
  emergencyAddress: { postalCode: string; pref: string; city: string; town: string; line2: string };

  // setters
  setLegalFamily: (v: string) => void;
  setLegalFirst: (v: string) => void;
  setLegalMiddle: (v: string) => void;
  setKanaFamily: (v: string) => void;
  setKanaFirst: (v: string) => void;
  setKanaMiddle: (v: string) => void;
  setDob: (v: { year: number; month: number; day: number }) => void;
  setGender: (v: 'male' | 'female' | 'other') => void;
  setNationality: (v: string) => void;
  setPhoneNumber: (v: string) => void;
  setHomePhoneNumber: (v: string) => void;
  setHomePhoneSkipped: (v: boolean) => void;
  setAddress: (v: { postalCode: string; pref: string; city: string; town: string; line2: string }) => void;
  setEmail: (v: string) => void;
  setResidenceCardNumber: (v: string) => void;
  setMyNumber: (v: string) => void;
  setMyNumberSkipped: (v: boolean) => void;
  setMaritalStatus: (v: MaritalStatus) => void;
  setFamilyStatusSkipped: (v: boolean) => void;
  setFamilyStatusSelections: (v: FamilyStatusFlag[]) => void;
  setDisabilityCategory: (v: DisabilityCategory) => void;
  setDisabilityFacts: (v: string) => void;
  setEmergencyHasContact: (v: YesNo) => void;
  setEmergencyName: (v: string) => void;
  setEmRelationship: (v: string) => void;
  setEmergencyPhone: (v: string) => void;
  setEmergencyAddress: (v: { postalCode: string; pref: string; city: string; town: string; line2: string }) => void;

  // validators (TextInput contract)
  validateLegalName: (v: string) => string | null;
  validateKana: (v: string) => string | null;
  validatePhone: (v: string) => string | null;
  validateEmail: (v: string) => string | null;
  validateResidenceCardNumber: (v: string) => string | null;
  validateMyNumber: (v: string) => string | null;

  // canSend
  canSendLegal: boolean;
  canSendKana: boolean;
  canSendGender: boolean;
  canSendPhone: boolean;
  canSendHomePhone: boolean;
  canSendAddress: boolean;
  canSendEmail: boolean;
  canSendResidenceCard: boolean;
  canSendMyNumber: boolean;
  canSendMaritalStatus: boolean;
  canSendFamilyStatus: boolean;
  canSendDisabilityCategory: boolean;
  canSendDisabilityFacts: boolean;
  canSendEmergencyContact: boolean;
  canSendEmergencyName: boolean;
  canSendEmergencyRelationship: boolean;
  canSendEmergencyPhone: boolean;
  canSendEmergencyAddress: boolean;

  // handlers
  handleSendLegal: () => void;
  handleSendKana: () => void;
  handleSendDob: () => void;
  handleSendGender: () => void;
  handleSendNationality: () => void;
  handleSendPhone: () => void;
  handleSendHomePhone: () => void;
  handleSkipHomePhone: () => void;
  handleSendAddress: () => void;
  handleSendEmail: () => void;
  handleSendResidenceCard: () => void;
  handleSendMyNumber: () => void;
  handleSkipMyNumber: () => void;
  handleSendMaritalStatus: () => void;
  handleSendFamilyStatus: () => void;
  handleSendDisabilityCategory: () => void;
  handleSendDisabilityFacts: () => void;
  handleSendEmergencyContact: () => void;
  handleSendEmergencyName: () => void;
  handleSendEmergencyRelationship: () => void;
  handleSendEmergencyPhone: () => void;
  handleSendEmergencyAddress: () => void;

  // options
  nationalityOptions: { value: string; label: string; keywords?: string[] }[];
}

export default function GeneralProfileChatComposer(p: GeneralProfileChatComposerProps) {
  if (p.step === 'legal') {
    return (
      <TextInput
        fields={[
          {
            id: 'family',
            label: p.t('submitDocuments.profileName.field.family'),
            autoComplete: 'family-name',
            required: true,
            validate: p.validateLegalName,
          },
          {
            id: 'first',
            label: p.t('submitDocuments.profileName.field.first'),
            autoComplete: 'given-name',
            required: true,
            validate: p.validateLegalName,
          },
          {
            id: 'middle',
            label: p.t('submitDocuments.profileName.field.middle'),
            autoComplete: 'additional-name',
            validate: p.validateLegalName,
          },
        ]}
        values={{ family: p.legalFamily, first: p.legalFirst, middle: p.legalMiddle }}
        onChange={(id, value) => {
          if (id === 'family') p.setLegalFamily(value);
          if (id === 'first') p.setLegalFirst(value);
          if (id === 'middle') p.setLegalMiddle(value);
        }}
        onSubmit={p.handleSendLegal}
        submitDisabled={!p.canSendLegal}
        submitAriaLabel={p.t('submitDocuments.profileName.send')}
        requiredMessage={p.t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (p.step === 'kana') {
    return (
      <TextInput
        fields={[
          { id: 'family', label: p.t('submitDocuments.profileName.field.kanaFamily'), required: true, validate: p.validateKana },
          { id: 'first', label: p.t('submitDocuments.profileName.field.kanaFirst'), required: true, validate: p.validateKana },
          { id: 'middle', label: p.t('submitDocuments.profileName.field.kanaMiddle'), validate: p.validateKana },
        ]}
        values={{ family: p.kanaFamily, first: p.kanaFirst, middle: p.kanaMiddle }}
        onChange={(id, value) => {
          if (id === 'family') p.setKanaFamily(value);
          if (id === 'first') p.setKanaFirst(value);
          if (id === 'middle') p.setKanaMiddle(value);
        }}
        onSubmit={p.handleSendKana}
        submitDisabled={!p.canSendKana}
        submitAriaLabel={p.t('submitDocuments.profileName.send')}
        requiredMessage={p.t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (p.step === 'dob') {
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <WheelDatePicker value={p.dob} onChange={p.setDob} locale={p.wheelLocale} />
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={p.handleSendDob}
            aria-label={p.t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (p.step === 'gender') {
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <ChoicePicker
            value={p.gender === 'unknown' ? null : p.gender}
            options={(['male', 'female', 'other'] as const).map((v) => ({
              value: v,
              label: p.t(`submitDocuments.profileName.gender.option.${v}`),
            }))}
            onChange={(v) => p.setGender(v)}
          />
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={p.handleSendGender}
            disabled={!p.canSendGender}
            aria-label={p.t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (p.step === 'nationality') {
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <SearchableListPicker
            value={p.nationality.trim() ? p.nationality : null}
            defaultValue="Japanese"
            options={p.nationalityOptions}
            onChange={(v) => p.setNationality(v)}
            placeholder={p.t('submitDocuments.profileName.nationalitySearchPlaceholder')}
          />
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={p.handleSendNationality}
            disabled={!p.nationality.trim()}
            aria-label={p.t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (p.step === 'phone') {
    return (
      <TextInput
        fields={[
          {
            id: 'phone',
            label: p.t('submitDocuments.profileName.field.phone'),
            autoComplete: 'tel',
            required: true,
            validate: p.validatePhone,
          },
        ]}
        values={{ phone: p.phoneNumber }}
        onChange={(id, value) => {
          if (id === 'phone') p.setPhoneNumber(value);
        }}
        onSubmit={p.handleSendPhone}
        submitDisabled={!p.canSendPhone}
        submitAriaLabel={p.t('submitDocuments.profileName.send')}
        requiredMessage={p.t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (p.step === 'homePhone') {
    const canSubmitHomePhone = p.homePhoneSkipped || p.canSendHomePhone;
    const homePhoneError =
      !p.homePhoneSkipped && p.homePhoneNumber.trim().length > 0 ? p.validatePhone(p.homePhoneNumber) : null;
    const handleSubmitHomePhone = () => {
      if (p.homePhoneSkipped) p.handleSkipHomePhone();
      else p.handleSendHomePhone();
    };

    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <ChoicePicker
            value={p.homePhoneSkipped ? 'skip' : null}
            options={[{ value: 'skip', label: p.t('submitDocuments.profileName.homePhone.skipped') }]}
            onChange={() => {
              p.setHomePhoneSkipped(true);
              p.setHomePhoneNumber('');
            }}
          />
          <div className="ff-text-input__row">
            <label className="ff-text-input__field">
              <span className="ff-text-input__label">{p.t('submitDocuments.profileName.field.homePhone')}</span>
              <input
                type="tel"
                className={`ff-text-input__input${homePhoneError ? ' ff-text-input__input--error' : ''}`}
                value={p.homePhoneNumber}
                onChange={(e) => {
                  p.setHomePhoneSkipped(false);
                  p.setHomePhoneNumber(e.target.value);
                }}
                autoComplete="tel"
                aria-invalid={Boolean(homePhoneError)}
              />
            </label>
            {homePhoneError && (
              <div className="ff-text-input__error" role="alert">
                {homePhoneError}
              </div>
            )}
          </div>
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={handleSubmitHomePhone}
            disabled={!canSubmitHomePhone}
            aria-label={p.t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (p.step === 'address') {
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <JpAddressInput value={p.address} onChange={p.setAddress} />
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={p.handleSendAddress}
            disabled={!p.canSendAddress}
            aria-label={p.t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (p.step === 'email') {
    return (
      <TextInput
        fields={[
          {
            id: 'email',
            label: p.t('submitDocuments.profileName.field.email'),
            autoComplete: 'email',
            required: true,
            validate: p.validateEmail,
          },
        ]}
        values={{ email: p.email }}
        onChange={(id, value) => {
          if (id === 'email') p.setEmail(value);
        }}
        onSubmit={p.handleSendEmail}
        submitDisabled={!p.canSendEmail}
        submitAriaLabel={p.t('submitDocuments.profileName.send')}
        requiredMessage={p.t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (p.step === 'residenceCard') {
    return (
      <TextInput
        fields={[
          {
            id: 'residenceCardNumber',
            label: p.t('submitDocuments.profileName.field.residenceCardNumber'),
            autoComplete: 'off',
            required: true,
            validate: p.validateResidenceCardNumber,
          },
        ]}
        values={{ residenceCardNumber: p.residenceCardNumber }}
        onChange={(id, value) => {
          if (id === 'residenceCardNumber') p.setResidenceCardNumber(value);
        }}
        onSubmit={p.handleSendResidenceCard}
        submitDisabled={!p.canSendResidenceCard}
        submitAriaLabel={p.t('submitDocuments.profileName.send')}
        requiredMessage={p.t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (p.step === 'myNumber') {
    const canSubmitMyNumber = p.myNumberSkipped || p.canSendMyNumber;
    const myNumberError =
      !p.myNumberSkipped && p.myNumber.trim().length > 0 ? p.validateMyNumber(p.myNumber) : null;
    const handleSubmitMyNumber = () => {
      if (p.myNumberSkipped) p.handleSkipMyNumber();
      else p.handleSendMyNumber();
    };

    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <ChoicePicker
            value={p.myNumberSkipped ? 'skip' : null}
            options={[{ value: 'skip', label: p.t('submitDocuments.profileName.myNumber.skipped') }]}
            onChange={() => {
              p.setMyNumberSkipped(true);
              p.setMyNumber('');
            }}
          />
          <div className="ff-text-input__row">
            <label className="ff-text-input__field">
              <span className="ff-text-input__label">{p.t('submitDocuments.profileName.field.myNumber')}</span>
              <input
                type="text"
                className={`ff-text-input__input${myNumberError ? ' ff-text-input__input--error' : ''}`}
                value={p.myNumber}
                onChange={(e) => {
                  p.setMyNumberSkipped(false);
                  p.setMyNumber(e.target.value);
                }}
                autoComplete="off"
                aria-invalid={Boolean(myNumberError)}
              />
            </label>
            {myNumberError && (
              <div className="ff-text-input__error" role="alert">
                {myNumberError}
              </div>
            )}
          </div>
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={handleSubmitMyNumber}
            disabled={!canSubmitMyNumber}
            aria-label={p.t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (p.step === 'maritalStatus') {
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <ChoicePicker
            value={p.maritalStatus || null}
            options={(['married', 'not_married'] as const).map((v) => ({
              value: v,
              label: p.t(`submitDocuments.profileName.maritalStatus.option.${v === 'married' ? 'married' : 'notMarried'}`),
            }))}
            onChange={(v) => p.setMaritalStatus(v)}
          />
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={p.handleSendMaritalStatus}
            disabled={!p.canSendMaritalStatus}
            aria-label={p.t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (p.step === 'familyStatus') {
    const familyOptions: { value: FamilyStatusFlag; label: string }[] = [];
    if (p.showWidowedFamilyOption) {
      familyOptions.push({
        value: 'widowed',
        label: p.t('submitDocuments.profileName.familyStatus.option.widowed'),
      });
    }
    familyOptions.push({
      value: 'single_parent',
      label: p.t('submitDocuments.profileName.familyStatus.option.singleParent'),
    });

    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <ChoicePicker
            value={p.familyStatusSkipped ? 'skip' : null}
            options={[{ value: 'skip', label: p.t('submitDocuments.profileName.familyStatus.skipped') }]}
            onChange={() => {
              p.setFamilyStatusSkipped(true);
              p.setFamilyStatusSelections([]);
            }}
          />
          <MultiChoicePicker
            values={p.familyStatusSelections}
            options={familyOptions}
            onChange={(v) => {
              p.setFamilyStatusSkipped(false);
              p.setFamilyStatusSelections(v);
            }}
          />
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={p.handleSendFamilyStatus}
            disabled={!p.canSendFamilyStatus}
            aria-label={p.t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (p.step === 'disabilityCategory') {
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <ChoicePicker
            value={p.disabilityCategory || null}
            options={(['none', 'general', 'special'] as const).map((v) => ({
              value: v,
              label: p.t(`submitDocuments.profileName.disabilityCategory.option.${v}`),
            }))}
            onChange={(v) => p.setDisabilityCategory(v)}
          />
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={p.handleSendDisabilityCategory}
            disabled={!p.canSendDisabilityCategory}
            aria-label={p.t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (p.step === 'disabilityFacts') {
    return (
      <TextInput
        fields={[
          {
            id: 'disabilityFacts',
            label: p.t('submitDocuments.profileName.disabilityFactsLabel'),
            required: true,
          },
        ]}
        values={{ disabilityFacts: p.disabilityFacts }}
        onChange={(id, value) => {
          if (id === 'disabilityFacts') p.setDisabilityFacts(value);
        }}
        onSubmit={p.handleSendDisabilityFacts}
        submitDisabled={!p.canSendDisabilityFacts}
        submitAriaLabel={p.t('submitDocuments.profileName.send')}
        requiredMessage={p.t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (p.step === 'emergencyContact') {
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <ChoicePicker
            value={p.emergencyHasContact || null}
            options={[
              { value: 'yes' as const, label: p.t('submitDocuments.profileName.emergency.yes') },
              { value: 'no' as const, label: p.t('submitDocuments.profileName.emergency.no') },
            ]}
            onChange={(v) => p.setEmergencyHasContact(v)}
          />
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={p.handleSendEmergencyContact}
            disabled={!p.canSendEmergencyContact}
            aria-label={p.t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (p.step === 'emergencyName') {
    return (
      <TextInput
        fields={[
          {
            id: 'emergencyName',
            label: p.t('submitDocuments.profileName.emergency.field.name'),
            required: true,
            validate: p.validateLegalName,
          },
        ]}
        values={{ emergencyName: p.emergencyName }}
        onChange={(id, value) => {
          if (id === 'emergencyName') p.setEmergencyName(value);
        }}
        onSubmit={p.handleSendEmergencyName}
        submitDisabled={!p.canSendEmergencyName}
        submitAriaLabel={p.t('submitDocuments.profileName.send')}
        requiredMessage={p.t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (p.step === 'emergencyRelationship') {
    return (
      <TextInput
        fields={[
          {
            id: 'emRelationship',
            label: p.t('submitDocuments.profileName.emergency.field.relationship'),
            required: true,
          },
        ]}
        values={{ emRelationship: p.emRelationship }}
        onChange={(id, value) => {
          if (id === 'emRelationship') p.setEmRelationship(value);
        }}
        onSubmit={p.handleSendEmergencyRelationship}
        submitDisabled={!p.canSendEmergencyRelationship}
        submitAriaLabel={p.t('submitDocuments.profileName.send')}
        requiredMessage={p.t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (p.step === 'emergencyPhone') {
    return (
      <TextInput
        fields={[
          {
            id: 'emergencyPhone',
            label: p.t('submitDocuments.profileName.emergency.field.phone'),
            autoComplete: 'tel',
            required: true,
            validate: p.validatePhone,
          },
        ]}
        values={{ emergencyPhone: p.emergencyPhone }}
        onChange={(id, value) => {
          if (id === 'emergencyPhone') p.setEmergencyPhone(value);
        }}
        onSubmit={p.handleSendEmergencyPhone}
        submitDisabled={!p.canSendEmergencyPhone}
        submitAriaLabel={p.t('submitDocuments.profileName.send')}
        requiredMessage={p.t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (p.step === 'emergencyAddress') {
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <JpAddressInput value={p.emergencyAddress} onChange={p.setEmergencyAddress} />
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={p.handleSendEmergencyAddress}
            disabled={!p.canSendEmergencyAddress}
            aria-label={p.t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  return null;
}

